import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Search, Wallet } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Count,
  EmptyState,
  Page,
  PageHeader,
  Pager,
  SortableHead,
  TableCard,
  Toolbar,
} from '@/components/shell'
import { ExportButton } from '@/components/export-button'
import { QuoteAmounts } from '@/components/quote-amounts'
import { WalletDetailSheet } from '@/features/wallets/wallet-detail-sheet'
import { api, type Paginated, type WalletRow } from '@/lib/api'
import { formatAssetBreakdown, formatDateTime, shortAddress } from '@/lib/format'

/** 支持从代币页带 address 跳过来直接定位某个发行人 */
type WalletsSearch = { address?: string }

export const Route = createFileRoute('/dashboard/wallets')({
  validateSearch: (search: Record<string, unknown>): WalletsSearch => ({
    address: typeof search.address === 'string' ? search.address : undefined,
  }),
  component: WalletsPage,
})

/**
 * 发行人画像（docs/01 §3 A2）。
 *
 * 主站没有账号体系，「用户」= 链上地址，所以这是只读风控视图：
 * 找出批量发垃圾币的地址、追踪违规 token 的关联发行人。
 */
function WalletsPage() {
  const { address: initialAddress } = Route.useSearch()

  const [page, setPage] = useState(1)
  const [address, setAddress] = useState(initialAddress ?? '')
  const [onlyWithHidden, setOnlyWithHidden] = useState(false)
  const [minLaunches, setMinLaunches] = useState('')
  const [sort, setSort] = useState<string | undefined>()
  const [detailAddress, setDetailAddress] = useState<string | null>(initialAddress ?? null)

  const { data, isLoading } = useQuery({
    queryKey: ['wallets', { page, address, onlyWithHidden, minLaunches, sort }],
    queryFn: () =>
      api.get<Paginated<WalletRow>>('/wallets', {
        page,
        perPage: 20,
        address: address || undefined,
        onlyWithHidden: onlyWithHidden ? 'true' : undefined,
        minLaunches: minLaunches || undefined,
        sort,
      }),
  })

  return (
    <Page>
      <PageHeader
        title="发行人"
        description="按发币地址聚合。「被下架」列是风控主信号——同一地址名下多个代币被下架，通常意味着批量制造垃圾币。募资额按计价币分列，不同精度的资产不做合计。"
        actions={
          <ExportButton
            filename="发行人"
            fetchPage={(p, pp) =>
              api.get<Paginated<WalletRow>>('/wallets', {
                page: p,
                perPage: pp,
                address: address || undefined,
                onlyWithHidden: onlyWithHidden ? 'true' : undefined,
                minLaunches: minLaunches || undefined,
                sort,
              })
            }
            columns={[
              { header: '地址', value: (w) => w.creator },
              { header: '发币数', value: (w) => w.launchCount },
              { header: '已毕业', value: (w) => w.graduatedCount },
              { header: '被下架', value: (w) => w.hiddenCount },
              {
                header: '累计募资（分币种）',
                value: (w) => formatAssetBreakdown(w.collectedByQuote, 6),
              },
              { header: '首次发币', value: (w) => formatDateTime(w.firstLaunchAt) },
              { header: '最近发币', value: (w) => formatDateTime(w.lastLaunchAt) },
            ]}
          />
        }
      />

      <Toolbar trailing={<Count value={data?.total} unit="个地址" />}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-mut2" />
          <Input
            value={address}
            onChange={(e) => {
              setAddress(e.target.value)
              setPage(1)
            }}
            placeholder="地址精确搜索"
            className="h-9 w-72 rounded-apple pl-9 font-mono text-[12.5px]"
          />
        </div>

        <Input
          type="number"
          min={0}
          value={minLaunches}
          onChange={(e) => {
            setMinLaunches(e.target.value)
            setPage(1)
          }}
          placeholder="最少发币数"
          className="h-9 w-32 rounded-apple text-[13px]"
        />

        <label className="flex items-center gap-2 text-[13px] text-mut">
          <Switch
            checked={onlyWithHidden}
            onCheckedChange={(v) => {
              setOnlyWithHidden(v)
              setPage(1)
            }}
          />
          只看有下架记录
        </label>
      </Toolbar>

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow className="border-line/50 hover:bg-transparent">
              <TableHead className="section-label pl-6">地址</TableHead>
              <SortableHead field="launchCount" sort={sort} onSort={setSort} align="right">
                发币数
              </SortableHead>
              <SortableHead field="graduatedCount" sort={sort} onSort={setSort} align="right">
                已毕业
              </SortableHead>
              <SortableHead field="hiddenCount" sort={sort} onSort={setSort} align="right">
                被下架
              </SortableHead>
              <TableHead className="section-label text-right">
                累计募资
                <span className="ml-1 font-mono text-[9.5px] normal-case text-dim">按计价币</span>
              </TableHead>
              <SortableHead field="firstLaunchAt" sort={sort} onSort={setSort}>
                首次发币
              </SortableHead>
              <SortableHead field="lastLaunchAt" sort={sort} onSort={setSort} className="pr-6">
                最近发币
              </SortableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i} className="border-line/40">
                  <TableCell colSpan={7} className="px-6 py-2.5">
                    <Skeleton className="h-7 w-full rounded-apple-sm" />
                  </TableCell>
                </TableRow>
              ))}

            {data?.data.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7}>
                  <EmptyState icon={Wallet} title="没有符合条件的地址" />
                </TableCell>
              </TableRow>
            )}

            {data?.data.map((w) => (
              <TableRow
                key={w.creator}
                // 有下架记录的整行标 warn，一眼可见
                className={`row-hover cursor-pointer border-line/40 ${
                  w.hiddenCount > 0 ? 'bg-warn/[0.04]' : ''
                }`}
                onClick={() => setDetailAddress(w.creator)}
              >
                <TableCell className="pl-6">
                  <button
                    className="pressable font-mono text-[11.5px] text-mut hover:text-cyan"
                    onClick={(e) => {
                      // 复制不该顺带打开抽屉
                      e.stopPropagation()
                      navigator.clipboard.writeText(w.creator)
                      toast.success('地址已复制')
                    }}
                    title={w.creator}
                  >
                    {shortAddress(w.creator, 10, 8)}
                  </button>
                </TableCell>
                <TableCell className="tnum text-right font-mono text-[12px] text-txt">
                  {w.launchCount}
                </TableCell>
                <TableCell className="tnum text-right font-mono text-[11.5px] text-mut">
                  {w.graduatedCount}
                </TableCell>
                <TableCell className="text-right">
                  {w.hiddenCount > 0 ? (
                    <Badge className="h-5 bg-warn/15 text-[10.5px] text-warn hover:bg-warn/15">
                      {w.hiddenCount}
                    </Badge>
                  ) : (
                    <span className="tnum font-mono text-[11.5px] text-dim">0</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <QuoteAmounts values={w.collectedByQuote} fractionDigits={2} />
                </TableCell>
                <TableCell className="tnum font-mono text-[11px] text-mut2">
                  {formatDateTime(w.firstLaunchAt)}
                </TableCell>
                <TableCell className="tnum pr-6 font-mono text-[11px] text-mut2">
                  {formatDateTime(w.lastLaunchAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      <Pager page={page} pageCount={data?.pageCount ?? 1} onChange={setPage} />

      <WalletDetailSheet
        address={detailAddress}
        onOpenChange={(open) => !open && setDetailAddress(null)}
      />
    </Page>
  )
}
